import React from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Legend,
  ReferenceDot,
  Label,
} from "recharts";
import GlobalData from "data/GlobalData";
import Step4Data from "data/Step4Data";

const generateData = (priceValue) => {
  const data = [];

  let valA;
  if (isNaN(priceValue.Acalc)) {
    valA = 0;
  } else {
    valA = priceValue.Acalc;
  }

  let valB;
  if (isNaN(priceValue.Bcalc)) {
    valB = 0;
  } else {
    valB = priceValue.Bcalc;
  }
  let valC;
  if (isNaN(priceValue.Ccalc)) {
    valC = 0;
  } else {
    valC = priceValue.Ccalc;
  }
  let valD;
  if (isNaN(priceValue.Dcalc)) {
    valD = 0;
  } else {
    valD = priceValue.Dcalc;
  }
  let valE;
  if (isNaN(priceValue.Ecalc)) {
    valE = 0;
  } else {
    valE = priceValue.Ecalc;
  }
  const cost =
    Number(valA) +
    Number(valB) +
    Number(valC) / 12 +
    Number(valD) +
    Number(valE) / 12;

  let intersectionPoint = null;

  for (let month = 1; month <= 60; month += 0.01) {
    const line1Value = month <= 6 ? month * cost : null;
    const line2Value = month > 7 ? (month - 6) * cost : null;
    const line3Value = month >= 6 ? Number(priceValue.priceValue) : null;

    // Check if Line 2 and Line 3 intersect
    if (
      line2Value !== null &&
      line3Value !== null &&
      intersectionPoint === null &&
      line2Value >= line3Value
    ) {
      const slope =
        (line2Value - data[data.length - 1]?.line2) /
        (month - data[data.length - 1]?.month);
      const intersectionMonth =
        data[data.length - 1].month +
        (line3Value - data[data.length - 1].line2) / slope;
      intersectionPoint = {
        month: intersectionMonth,
        line2: line2Value,
        line3: line3Value,
      };
    }

    data.push({
      month,
      line1: line1Value,
      line2: line2Value,
      line3: line3Value,
    });
  }

  return { data, intersectionPoint };
};

const ChartComponent = (priceValue) => {
  const { data, intersectionPoint } = generateData(priceValue);
  const CustomYAxisTick = (props) => {
    const { x, y, payload } = props;
    const formattedValue = payload.value.toLocaleString("pl-PL", {
      style: "decimal",
    });

    return (
      <g transform={`translate(${x},${y})`}>
        <text
          x={0}
          y={0}
          dy={16}
          textAnchor="end"
          fill="#666"
          style={{ fontSize: "10px" }}
          transform="rotate(-35)"
        >
          {formattedValue}
        </text>
      </g>
    );
  };
  let name1 = "";
  let name2 = "";
  let name3 = "";
  if (priceValue.lang === "pl") {
    name1 = Step4Data.novision.pl;
    name2 = Step4Data.savings.pl;
    name3 = Step4Data.costvision.pl;
  } else if (priceValue.lang === "en") {
    name1 = Step4Data.novision.en;
    name2 = Step4Data.savings.en;
    name3 = Step4Data.costvision.en;
  }
  return (
    <LineChart height={500} width={760} data={data}>
      {/* X-Axis */}
      <XAxis
        dataKey="month"
        type="number"
        ticks={[6, 12, 18, 24, 30, 36, 42, 48, 54, 60]}
        tickCount={10}
      >
        <Label
          value={
            priceValue.lang === "pl" ? GlobalData.month.pl : GlobalData.month.en
          }
          position="insideBottomRight"
          offset={-5}
        />
      </XAxis>

      {/* Y-Axis */}
      <YAxis
        type="number"
        tick={<CustomYAxisTick />}
        tickCount={8}
        interval={"preserveStartEnd"}
      >
        <Label
          value={priceValue.lang === "pl" ? "PLN" : "EUR"}
          position="insideTopLeft"
          offset={-5}
        />
      </YAxis>

      {/* Grid */}
      <CartesianGrid strokeDasharray="3 3" />

      {/* Lines */}
      <Line
        dot={false}
        type="monotone"
        dataKey="line1"
        name={name1}
        stroke="#FF0000"
        strokeWidth={3}
        animationDuration={0}
      />
      <Line
        dot={false}
        type="monotone"
        dataKey="line2"
        name={name2}
        stroke="#097969"
        strokeWidth={3}
        animationDuration={0}
      />
      <Line
        dot={false}
        type="monotone"
        dataKey="line3"
        name={name3}
        stroke="#89CFF0"
        strokeWidth={3}
        animationDuration={0}
      />

      {/* Intersection Dot */}
      {intersectionPoint && (
        <ReferenceDot
          x={intersectionPoint.month}
          y={intersectionPoint.line2}
          r={4}
          fill="green"
          shape={<circle r={4} fill="green" />}
          stroke="none"
          label={{
            value: `ROI: ${priceValue.roi.toFixed(2)}`,
            position: "right",
            offset: 5,
            fill: "green",
          }}
          animationDuration={0}
        />
      )}
      <Legend />
    </LineChart>
  );
};

export default ChartComponent;
